import { OrderDetailAttributes, VisitAttributes } from "@albatrosdeveloper/ave-models-package";
import { OrderStatusERPEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/status.enum";
import { CommonModule, DecimalPipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import {
  MatPaginatorIntl,
  MatPaginatorModule,
} from "@angular/material/paginator";
import { MatSidenavContainer } from "@angular/material/sidenav";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { IPaginationResult } from "app/common/interfaces";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import {
  ExportDialogComponent,
  ExportOptions,
} from "app/layout/export-overlay";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { CompanyService } from "app/modules/components/configuration/company/company.service.service";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { ConfigurationFiltersComponent } from "app/modules/components/filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "app/modules/components/filters/documents-filters/documents-filters.component";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { MaterialsManagementFiltersComponent } from "app/modules/components/filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "app/modules/components/filters/status-filters/status-filters.component";
import { DocumentFlowButtonComponent } from "app/modules/shared/document-flow-button/document-flow-button.component";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { DecimalConfigService } from "app/services/decimal-config.service";

// Smart Grid imports
import {
  SmartGridComponent,
  ColDef,
  GridConfig,
  PageChangedEvent,
  SortChangedEvent,
  GridReadyEvent,
} from 'app/shared/components/smart-grid';

// Cell Renderers
import { RouteUserCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/route-user-cell-renderer/route-user-cell-renderer.component';
import { CustomerCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component';
import { MaterialCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component';
import { QuantityCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/quantity-cell-renderer/quantity-cell-renderer.component';
import { DateHourCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component';
import { CurrencyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component';
import { ActionsCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/actions-cell-renderer/actions-cell-renderer.component';
import { ChargeInOutSerialNavigationCellRendererComponent } from './cell-renderers/serial-navigation-cell-renderer.component';
import { OrderCustomerExport } from "../order-customer/exports/order-customer.export";
import { DateHelper } from "app/utils/date.helper";
import { debounceTime, skip, Subscription } from "rxjs";
import { getCode } from "app/utils/method";
import { ORDER_CHARGE_IN_OUT_EXPORT_ID, ORDER_CHARGE_IN_OUT_TITLE } from "../order-customer/exports/order-customer.constants";
import { OrderCustomerService } from "../order-customer/order-customer.service";
import { BusinessPartnerCellRendererComponent } from "app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component";
import { TripLiquidationCellRendererComponent } from "../../liquidations/cell-renderers/trip-liquidation-cell-renderer.component";
import { Router } from "@angular/router";
import { TripCellRendererComponent } from "../../visits/visit-report/cell-renderers/trip-cell-renderer.component";


@Component({
  selector: "app-charge-in-out",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatMenuModule,
    FilterSectionComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    FilterComponent,
    HoverMenuDirective,
    SmartGridComponent,
  ],
  providers: [DecimalPipe, OrderCustomerExport],
  templateUrl: "./charge-in-out.component.html",
})
export class ChargeInOutComponent implements OnInit, OnDestroy {
  private isInitialized = false;
  private decimalPipe = new DecimalPipe("en-US");

  UMPActive: boolean;
  type = "DT003";

  // Smart Grid Configuration
  columnDefs: ColDef<any>[] = [
    {
      colId: "routeUser",
      field: "subRoute.code" as any,
      pinned: "left",
      headerName: "Ruta",
      cellRenderer: RouteUserCellRendererComponent,
      minWidth: 300,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => {
          const routeName = item.subRoute?.name ?? "-";
          const userName = item.user?.name ?? "-";
          return `${routeName} - ${userName}`;
        },
        customFilterFn: (rowData: any, filterValue: string) => {
          const routeName = rowData.subRoute?.name ?? "-";
          const userName = rowData.user?.name ?? "-";
          const displayValue = `${routeName} - ${userName}`;
          if (!displayValue || displayValue === "- - -") return !filterValue;
          return displayValue === filterValue;
        },
      },
    },    
    {
      field: 'trip.id',
      headerName: 'Viaje',
      minWidth: 150,
      sortable: true,
      cellRenderer: TripCellRendererComponent,
      cellRendererParams: {
       onTripClick: (data: any) => this.goToTravel(data)
      }
    },
    {
      field: "serials",
      headerName: "Nro. Doc.",
      cellRenderer: ChargeInOutSerialNavigationCellRendererComponent,
      cellRendererParams: {
        documentType: this.type, // 'DT003' (Carga In/Out)
      },
      minWidth: 160,
      sortable: true,
    },
    {
      field: "date",
      headerName: "Fecha",
      cellRenderer: DateHourCellRendererComponent,
      minWidth: 180,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => {
          if (!item.date) return "-";
          const date = new Date(item.date);
          return date.toLocaleDateString("es-ES");
        },
        customFilterFn: (rowData: any, filterValue: string) => {
          if (!rowData.date) return !filterValue;
          const date = new Date(rowData.date);
          const formattedDate = date.toLocaleDateString("es-ES");
          return formattedDate === filterValue;
        },
      },
    },
    {
      field: "soldTo.name",
      headerName: "Pagador/Receptor",
      cellRenderer: BusinessPartnerCellRendererComponent,
      autoSize: true,
      minWidth: 250, // Much wider for code + long name
      sortable: true,
      filterOptions: {
        // Display customer name in filter dropdown instead of code
        // Note: Requires shipToName field to be present in row data
        valueExtractor: (item: any) => item?.soldTo?.name,
        customFilterFn(rowData, filterValue) {
          const rowCustomer = rowData?.soldTo?.name;
          if (!rowCustomer) return !filterValue;

          return rowCustomer === filterValue;
        },
      },
    },
    {
      field: "materialName.name" as any,
      headerName: "Material",
      cellRenderer: MaterialCellRendererComponent,
      minWidth: 300,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => item.materialName?.name ?? "-",
        customFilterFn: (rowData: any, filterValue: string) => {
          const materialName = rowData.materialName?.name ?? "-";
          if (!materialName || materialName === "-") return !filterValue;
          return materialName === filterValue;
        },
      },
    },
    {
      field: "soldQuantity",
      headerName: "Cantidad",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        getUMPActive: () => this.UMPActive,
      },
      minWidth: 180,
      sortable: true,
    },
    {
      field: "unitPrice",
      headerName: "Precio",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("unitPrice"),
    },
    {
      field: "subTotal",
      headerName: "Subtotal",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("subTotal"),
    },
    {
      field: "total",
      headerName: "Total",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("total"),
    },
    {
      colId: "actions",
      headerName: "",
      cellRenderer: ActionsCellRendererComponent,
      cellClass: "text-center",
      headerClass: "text-center",
      minWidth: 80,
      width: 80,
      sortable: false,
      pinned: "right",
    },
  ];

  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "_id" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  // Data
  data: OrderDetailAttributes[] = [];
  loading: boolean = false;

  //#region Filter
  totalItems: number = 0;
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "_id";
  sortDirection: "ASC" | "DESC" = "DESC";
  //#endregion

  //#region properties

  //BUSCADOR
  searchSubscription: Subscription;
  //#endregion

  getCode = getCode;

  //Para filtros
  selectedStatuses: number[] = [];

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];
  form: FormGroup;

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  OrderStatusERPEnum = OrderStatusERPEnum;
  StatusEnum = StatusEnum;

  constructor(
    private orderCustomer: OrderCustomerService,
    private readonly searchService: SearchService,
    private readonly orderCustomerExport: OrderCustomerExport,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private fb: FormBuilder,
    private readonly companyService: CompanyService,
    private readonly companyCurrencyPipe: CompanyCurrencyPipe,
    private readonly decimalConfigService: DecimalConfigService,
    private readonly router: Router
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subRouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
      startDeliveryDate: [],
      endDeliveryDate: [],
      pickerUpload_start: [],
      pickerUpload_end: [],
    });
  }

  goToTravel(element: VisitAttributes) {
        if (element.trip?.id) {
          const queryParams = { tripId: element.trip.id };
          const urlTree = this.router.createUrlTree(['monitor', 'principal-screen'], { queryParams });
          const absoluteUrl = window.location.origin + this.router.serializeUrl(urlTree);
          window.open(absoluteUrl, '_blank');
        }
      }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.onHandleSearch();
    this.fetchCompanies();
    this.getList();
    this.isInitialized = true;
  }

  onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300))
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  private getSortField(): keyof OrderDetailAttributes {

    return this.sortActive;
  }

  onFilter() {
    this.getList();
  }
  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      if (res.data.length === 1) {
        this.UMPActive = res.data[0].UMPActive;
        this.companyService.setActiveCompany(res.data[0]);
      }
    });
  }

  /**
   * Smart Grid: Handle page change event (API mode)
   */
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1; // Convert 0-based to 1-based
    this.pageSize = event.pageSize;
    this.getList();
  }

  /**
   * Smart Grid: Handle sort change event (API mode)
   */
  onSortChanged(event: SortChangedEvent<OrderDetailAttributes>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as keyof OrderDetailAttributes;
      this.sortDirection = sortState.direction === "asc" ? "ASC" : "DESC";
    } else {
      this.sortActive = "_id";
      this.sortDirection = "DESC";
    }
    this.getList();
  }

  /**
   * Smart Grid: Handle grid ready event
   */
  onGridReady(event: GridReadyEvent<OrderDetailAttributes>): void {
    // Can be used for additional initialization if needed
  }

  getList() {
    this.loading = true;
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      type: this.type,
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate: DateHelper.getEndOfDay(this.form.get("end")?.value)
        ? DateHelper.getEndOfDay(this.form.get("end")?.value)
        : DateHelper.getEndOfDay(this.form.get("start")?.value)
          ? DateHelper.getEndOfDay(this.form.get("start")?.value)
          : null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip: DateHelper.getEndOfDay(this.form.get("endTrip")?.value)
        ? DateHelper.getEndOfDay(this.form.get("endTrip")?.value)
        : DateHelper.getEndOfDay(this.form.get("startTrip")?.value)
          ? DateHelper.getEndOfDay(this.form.get("startTrip")?.value)
          : null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip: DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value)
        ? DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value)
        : DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value)
          ? DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value)
          : null,
      startDeliveryDate: DateHelper.getStartOfDay(
        this.form.get("startDeliveryDate")?.value
      ),
      endDeliveryDate: DateHelper.getEndOfDay(
        this.form.get("endDeliveryDate")?.value
      )
        ? DateHelper.getEndOfDay(this.form.get("endDeliveryDate")?.value)
        : DateHelper.getEndOfDay(this.form.get("startDeliveryDate")?.value)
          ? DateHelper.getEndOfDay(this.form.get("startDeliveryDate")?.value)
          : null,
      pickerUpload_start: DateHelper.getStartOfDay(
        this.form.get("pickerUpload_start")?.value
      ),
      pickerUpload_end: DateHelper.getEndOfDay(
        this.form.get("pickerUpload_end")?.value
      )
        ? DateHelper.getEndOfDay(this.form.get("pickerUpload_end")?.value)
        : DateHelper.getEndOfDay(this.form.get("pickerUpload_start")?.value)
          ? DateHelper.getEndOfDay(this.form.get("pickerUpload_start")?.value)
          : null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      ordersToFilter: null,
    };

    this.orderCustomer.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        this.data = res?.data?.[0]?.data ?? [];
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.loading = false;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
        this.loading = false;
      },
    });
  }

  //#region Action
  onExportButtonPressed() {
    if (!this.orderCustomerExport.canStartTask()) return;
    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: ORDER_CHARGE_IN_OUT_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.orderCustomerExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          ORDER_CHARGE_IN_OUT_EXPORT_ID,
          ORDER_CHARGE_IN_OUT_TITLE
        );
      }
    });
  }
  onClearFilter() {
    this.form.reset({
      start: new Date(),
      end: new Date(),
    });
    this.onFilter();
  }

  //#endregion

  /**
   * Helper: Format currency values for display and filtering
   */
  private formatCurrency(value: number): string {
    if (value == null) return "-";
    const decimals = this.decimalConfigService.getDecimals();
    const formattedNum =
      this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? "-";
    if (formattedNum === "-") return "-";
    const symbol = this.companyCurrencyPipe.transform("symbol");
    return `${symbol} ${formattedNum}`;
  }

  /**
   * Helper: Create filter options for currency columns
   */
  private createCurrencyFilterOptions(fieldPath: string) {
    return {
      valueExtractor: (item: any) => {
        const value = item[fieldPath];
        return this.formatCurrency(value);
      },
      customFilterFn: (rowData: any, filterValue: string) => {
        const value = rowData[fieldPath];
        const formattedValue = this.formatCurrency(value);
        if (!formattedValue || formattedValue === "-") return !filterValue;
        return formattedValue === filterValue;
      },
    };
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
